import { UseQueryOptions, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { isDataAccessRequest } from 'modules/requests/utils'
import { getFileContent, getResultDetails, listBucketContents } from 'services/results'
import { ResultsQueryKeys } from 'services/results/queryKeys'
import type { GetFileContentResponse, Result } from 'services/results/types'
import { ApiErrorResponse } from 'services/types'

import { QueryKeys } from './queryKeys'
import useMultiQcFetcher from './useMultiQcFetcher'

export interface S3BrowserConfigV2 {
  resultId: string
  getResultDetailsOptions?: Omit<UseQueryOptions<Result | undefined, ApiErrorResponse, Result, string[]>, 'queryKey'>
}

export interface S3BrowserReturnV2 {
  breadcrumbs: string[]
  bucket: string | undefined
  prefix: string | undefined
  provider: string | undefined
  isLoading: boolean
  contents: (S3FileAsContent | S3DirAsContent)[]
  reports: {
    multiQc: string | undefined
    htmlFileContent: string | undefined
  }
  openDir(dirName: string): Promise<void>
  goUpOneLevel(): Promise<void>
  goToBreadcrumb(atIdx: number): Promise<void>
  isFile: boolean
  result?: Result
}

export const useS3BrowserV2 = ({ resultId, getResultDetailsOptions }: S3BrowserConfigV2): S3BrowserReturnV2 => {
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>()

  const runQuery = useQuery<Result | undefined, ApiErrorResponse, Result, string[]>({
    queryKey: ResultsQueryKeys.ResultDetails(resultId),
    queryFn: () => getResultDetails(resultId),
    retry: false && !!resultId,
    ...getResultDetailsOptions
  })

  const bucket: string | undefined = runQuery.data?.beforeCheckLocation.bucket
  const rootPrefix: string | undefined = runQuery.data?.beforeCheckLocation.prefix
  const provider: string | undefined = runQuery.data?.beforeCheckLocation.provider
  const isFile: boolean = !!runQuery.data?.beforeCheckLocation.isFile
  const isHTMLFile: boolean = !!(isFile && rootPrefix && rootPrefix.indexOf('.html') === rootPrefix.length - 5)
  /**
   * Data access requests reuse the Result schema and persist a placeholder `beforeCheckLocation`,
   * so there is no storage to browse and the listing would always fail.
   */
  const isBrowsable: boolean = !!runQuery.data && !isDataAccessRequest(runQuery.data)

  const s3BrowseQuery = useQuery<S3BucketResponse | undefined>({
    queryKey: QueryKeys.ListBucketContents(resultId),
    queryFn: () =>
      listBucketContents(resultId, bucket!, breadcrumbs ? [rootPrefix!, breadcrumbs.join('/')].join('/') : rootPrefix!),
    enabled: isBrowsable && !!bucket && !!rootPrefix,
    retry: false
  })

  useEffect(() => {
    if (breadcrumbs !== undefined) {
      s3BrowseQuery.refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breadcrumbs])

  const { data: htmlFileContent, isLoading: isLoadingHtmlFileContent } = useQuery<GetFileContentResponse | undefined>({
    queryKey: ['html-file-content', bucket, resultId],
    queryFn: () => getFileContent({ runId: resultId, bucket: bucket!, fileKey: rootPrefix! }),
    enabled: isHTMLFile && !!bucket && !!rootPrefix,
    retry: false
  })

  const openDir = async (dirName: string) => {
    setBreadcrumbs(prev => [...(prev ?? []), dirName])
  }

  const goUpOneLevel = async () => {
    setBreadcrumbs(prev => {
      const newBreadcrumbs = [...(prev ?? [])]
      newBreadcrumbs.pop()
      return newBreadcrumbs
    })
  }

  const goToBreadcrumb = async (atIdx: number) => {
    setBreadcrumbs(prev => {
      if (atIdx >= (prev?.length ?? 0) || atIdx < 0) throw Error('Cannot go to unexisting breadcrumb.')
      return [...(prev ?? [])].slice(0, atIdx + 1)
    })
  }

  const result: Result | undefined = runQuery.data

  const multiQcFileContent: string | undefined = useMultiQcFetcher({
    runId: resultId,
    bucket,
    rootUrl: rootPrefix,
    enabled: !!result?.resourceTypeId
  })

  return {
    result,
    breadcrumbs: breadcrumbs ?? [],
    bucket,
    prefix: rootPrefix,
    provider,
    isLoading: s3BrowseQuery.isLoading || runQuery.isLoading || isLoadingHtmlFileContent,
    contents: s3BrowseQuery.data?.contents || [],
    openDir,
    goUpOneLevel,
    goToBreadcrumb,
    isFile,
    reports: {
      htmlFileContent: htmlFileContent?.fileContent,
      multiQc: multiQcFileContent
    }
  }
}

export default useS3BrowserV2
